import { useEffect, useMemo, useRef, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import { addToast } from '@/app/slices/toast';
import { CreateOutsideEmbryosWizard } from '@/components';
import { WorkflowTabBar } from '@/components/common';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { EMBRYO_STATUS } from '@/pages/fresh-straw/constants';
import { queryClient } from '@/services';
import { embryoServiceApi } from '@/services/embryo-service/api';

import { AddImplantedEmbryosDialog } from './components/AddImplantedEmbryosDialog';
import { AddMissingStrawsDialog } from './components/AddMissingStrawsDialog';
import { EditEmbryoDialog } from './components/EditEmbryoDialog';
import { EmbryoReportsDropdown } from './components/EmbryoReportsDropdown';
import { LocationChangeDialog } from './components/LocationChangeDialog';
import { TabContent } from './components/TabContent';
import {
  EmbryoAppProvider,
  useEmbryoAppContext,
} from './context/EmbryoAppContext';
import {
  useEmbryoInventoryColumns,
  useIvfTransitEmbryosColumns,
} from './util/columns';
import {
  type ButtonAction,
  tabConfig,
  type TabRoute,
  tabRoutes,
  workflowTabs,
} from './util/constants';
import { useEmbryoDataSource } from './util/embryo-data-source';
import { useOutsideEmbryoDataSource } from './util/outside-embryo-data-source';

const EmbryoAppContent = () => {
  const isSelectedEmbryosShippingEnabled = useFeatureFlag(
    'Selected_Embryos_Shipping_Integration',
  );
  const isEmbryoReportsEnabled = useFeatureFlag('embryo_reports');

  const embryoInventoryColumns = useEmbryoInventoryColumns();
  const ivfTransitColumns = useIvfTransitEmbryosColumns();

  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const {
    selectedEmbryos,
    setSelectedEmbryos,
    selectedEmbryosRef,
    filters,
    setLoading,
    updateFilter,
  } = useEmbryoAppContext();

  useEffect(() => {
    if (!isSelectedEmbryosShippingEnabled) return;
    const siteIdParam = searchParams.get('siteId');
    if (siteIdParam && !filters.siteId) {
      updateFilter('siteId', siteIdParam);
    }
  }, [
    filters.siteId,
    isSelectedEmbryosShippingEnabled,
    searchParams,
    updateFilter,
  ]);

  const { dataSource: embryoDs } = useEmbryoDataSource(filters, setLoading);
  const { dataSource: ivfDs } = useEmbryoDataSource(filters, setLoading, {
    isIvfTransit: true,
  });
  const { dataSource: outsideDs } = useOutsideEmbryoDataSource(
    filters,
    setLoading,
  );

  const dataSourceByRoute: Record<TabRoute, any> = {
    embryos: embryoDs,
    'shipping-integration': embryoDs,
    'ivf-transit-embryos': ivfDs,
    'outside-embryos': outsideDs,
  };

  const [activeTab, setActiveTab] = useState<TabRoute>('embryos');
  const [locationChangeDialogVisible, setLocationChangeDialogVisible] =
    useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showCreateOutsideEmbryoDialog, setShowCreateOutsideEmbryoDialog] =
    useState(false);

  const refreshGridRef = useRef<(() => void) | null>(null);
  const deselectAllRef = useRef<(() => void) | null>(null);
  const editDialogInitialTabRef = useRef<string>('general');
  const { t } = useTranslation();

  const [addStrawsDialogVisible, setAddStrawsDialogVisible] = useState(false);
  const [
    addImplantedEmbryosDialogVisible,
    setAddImplantedEmbryosDialogVisible,
  ] = useState(false);

  const hasRestrictedEmbryos = selectedEmbryos.some(
    (e) =>
      e.status === EMBRYO_STATUS.SHIPPED ||
      e.status === EMBRYO_STATUS.IMPLANTED,
  );

  const currentTabConfig = tabConfig.find((tab) => tab.route === activeTab);

  const currentColumnDefs = useMemo(
    () =>
      getCurrentColumnDefs(
        activeTab,
        currentTabConfig?.columnDefs,
        embryoInventoryColumns,
        ivfTransitColumns,
      ),
    [activeTab, currentTabConfig, embryoInventoryColumns, ivfTransitColumns],
  );

  useEffect(() => {
    const pathSegments = location.pathname.split('/').filter(Boolean);
    const currentRoute = pathSegments[pathSegments.length - 1];

    if (!currentRoute || currentRoute === 'embryo') {
      setActiveTab('embryos');
      return;
    }

    if (tabRoutes.includes(currentRoute as TabRoute)) {
      setActiveTab(currentRoute as TabRoute);
    }
  }, [location.pathname]);

  const handleTabChange = (newTab: TabRoute) => {
    setActiveTab(newTab);
    const urlParams = new URLSearchParams(location.search);
    const search = urlParams.toString();

    const path = newTab === 'embryos' ? '/embryo' : `/embryo/${newTab}`;
    if (isSelectedEmbryosShippingEnabled && newTab === 'shipping-integration') {
      const embryosToShip = selectedEmbryosRef.current;
      navigate(`${path}${search ? `?${search}` : ''}`, {
        state: { shippingEmbryos: embryosToShip },
      });
    } else {
      setSelectedEmbryos([]);
      navigate(`${path}${search ? `?${search}` : ''}`, { replace: true });
    }
  };

  const handleEditSuccess = (failedEmbryos?: any[]) => {
    setSelectedEmbryos(failedEmbryos ?? []);
    deselectAllRef.current?.();
    refreshGridRef.current?.();
  };

  const handleActionButtonClick = (action: ButtonAction) => {
    switch (action) {
      case 'add-to-shipment': {
        if (isSelectedEmbryosShippingEnabled) {
          const embryosToShip = selectedEmbryosRef.current;
          const search = location.search;
          navigate(`/embryo/shipping-integration${search}`, {
            state: { shippingEmbryos: embryosToShip },
          });
        } else {
          navigate('/embryo/shipping-integration');
        }
        break;
      }
      case 'location-change':
        setLocationChangeDialogVisible(true);
        break;
      case 'add-outside-embryos':
        setShowCreateOutsideEmbryoDialog(true);
        break;
      case 'add-missing-straws':
        setAddStrawsDialogVisible(true);
        break;
      case 'add-implant-embryos':
        setAddImplantedEmbryosDialogVisible(true);
        break;
    }
  };

  const handleAddStrawsConfirm = async (
    entries: { count: number; stage?: number; grade?: number }[],
  ) => {
    if (selectedEmbryos.length === 1) {
      const outsideEmbryoId = selectedEmbryos[0].id;
      if (!outsideEmbryoId) {
        addToast({
          severity: 'error',
          summary: 'Error',
          detail: 'Unable to add straws for the selected outside embryo.',
        });
        return;
      }
      setAddStrawsDialogVisible(false);
      try {
        await embryoServiceApi.addStrawsV2({ outsideEmbryoId, entries });
        await queryClient.invalidateQueries({
          queryKey: ['useGetFreezeEntries', 'freeze-entries', outsideEmbryoId],
        });
        refreshGridRef.current?.();
        addToast({
          severity: 'success',
          summary: 'Straws Added',
          detail: 'Straws successfully added to outside embryo.',
        });
      } catch (error) {
        addToast({
          severity: 'error',
          summary: 'Error',
          detail:
            error instanceof Error ? error.message : 'Failed to add straws.',
        });
      }
    }
  };

  const handleAddImplantedEmbryosConfirm = async (
    entries: { count: number; stage?: number; grade?: number }[],
  ) => {
    if (selectedEmbryos.length === 1) {
      const outsideEmbryoId = selectedEmbryos[0].id;
      if (!outsideEmbryoId) {
        addToast({
          severity: 'error',
          summary: 'Error',
          detail:
            'Unable to add implanted embryos for the selected outside embryo.',
        });
        return;
      }
      setAddImplantedEmbryosDialogVisible(false);
      try {
        await embryoServiceApi.addImplantedEmbryosV2({
          outsideEmbryoId,
          entries,
        });
        await queryClient.invalidateQueries({
          queryKey: ['embryo', 'implanted', outsideEmbryoId],
        });
        refreshGridRef.current?.();
        addToast({
          severity: 'success',
          summary: 'Implanted Embryos Added',
          detail: 'Implanted embryos successfully added to outside embryo.',
        });
      } catch (error) {
        addToast({
          severity: 'error',
          summary: 'Error',
          detail:
            error instanceof Error
              ? error.message
              : 'Failed to add implanted embryos.',
        });
      }
    }
  };

  return (
    <div className="flex h-full flex-col">
      <div className="flex flex-col gap-2 bg-white p-2">
        <div className="flex items-center justify-between gap-2">
          {activeTab === 'embryos' && isEmbryoReportsEnabled && (
            <EmbryoReportsDropdown selectedEmbryos={selectedEmbryos} />
          )}

          <div className="ml-auto flex flex-wrap justify-end gap-2">
            {currentTabConfig?.buttons?.map((btn) => {
              const rowRestriction =
                btn.action !== 'add-outside-embryos'
                  ? selectedEmbryos.length < 1
                  : false;

              const isAddMissingStrawDisabled =
                (btn.action === 'add-missing-straws' ||
                  btn.action === 'add-implant-embryos') &&
                selectedEmbryos.length !== 1;

              const isRestricted =
                (btn.action === 'location-change' && hasRestrictedEmbryos) ||
                rowRestriction ||
                isAddMissingStrawDisabled;

              const showRestrictedTooltip =
                btn.action === 'location-change' && hasRestrictedEmbryos;

              const button = (
                <Button
                  variant={
                    btn.action === 'add-to-shipment' ? 'default' : 'outline'
                  }
                  onClick={() => handleActionButtonClick(btn.action)}
                  disabled={isRestricted}
                >
                  {btn.icon && <i className={`${btn.icon} text-xs`} />}
                  {btn.label}
                </Button>
              );

              return showRestrictedTooltip ? (
                <Tooltip key={btn.label}>
                  <TooltipTrigger
                    render={<span className="inline-flex">{button}</span>}
                  />
                  <TooltipContent>
                    {t('embryo.cannotChangeLocationOfRestrictedEmbryos')}
                  </TooltipContent>
                </Tooltip>
              ) : (
                <span key={btn.label} className="inline-flex">
                  {button}
                </span>
              );
            })}
          </div>
        </div>

        <WorkflowTabBar
          tabs={workflowTabs}
          activeTab={activeTab}
          onTabChange={handleTabChange}
          className="w-full"
        />
      </div>

      <div className="flex-1 overflow-hidden">
        {currentTabConfig && (
          <TabContent
            key={currentTabConfig.route}
            columnDefs={currentColumnDefs as any}
            storageKey={currentTabConfig.storageKey}
            gridClassName={currentTabConfig.gridClassName}
            dataSource={dataSourceByRoute[activeTab]}
            onGridReady={({ refresh, deselectAll }) => {
              refreshGridRef.current = refresh;
              deselectAllRef.current = deselectAll;
            }}
            onEditSelected={() => {
              editDialogInitialTabRef.current = 'general';
              setShowEditDialog(true);
            }}
            onDeactivateSelected={() => {
              editDialogInitialTabRef.current = 'additional';
              setShowEditDialog(true);
            }}
          />
        )}
      </div>

      <LocationChangeDialog
        visible={locationChangeDialogVisible}
        onHide={() => setLocationChangeDialogVisible(false)}
        selectedEmbryos={selectedEmbryos}
        onSuccess={(failedEmbryos) => {
          setSelectedEmbryos(failedEmbryos ?? []);
          deselectAllRef.current?.();
          refreshGridRef.current?.();
        }}
      />

      <EditEmbryoDialog
        visible={showEditDialog}
        selectedEmbryos={selectedEmbryos as any}
        onHide={() => setShowEditDialog(false)}
        onSuccess={handleEditSuccess}
        initialTab={editDialogInitialTabRef.current as any}
      />

      <Dialog
        open={showCreateOutsideEmbryoDialog}
        onOpenChange={setShowCreateOutsideEmbryoDialog}
      >
        <DialogContent className="flex h-[90vh] w-[95vw] max-w-[1200px] flex-col gap-0 p-0 md:h-[85vh] md:max-w-[1200px]">
          <DialogHeader className="shrink-0 border-b px-6 py-3">
            <DialogTitle>Add Outside Embryos</DialogTitle>
          </DialogHeader>
          <div className="min-h-0 flex-1 overflow-hidden px-6 pt-5">
            <CreateOutsideEmbryosWizard
              onCancel={() => setShowCreateOutsideEmbryoDialog(false)}
              onSuccess={() => {
                setShowCreateOutsideEmbryoDialog(false);
                refreshGridRef.current?.();
              }}
            />
          </div>
        </DialogContent>
      </Dialog>

      <AddMissingStrawsDialog
        visible={addStrawsDialogVisible}
        onHide={() => setAddStrawsDialogVisible(false)}
        onConfirm={handleAddStrawsConfirm}
      />
      <AddImplantedEmbryosDialog
        visible={addImplantedEmbryosDialogVisible}
        onHide={() => setAddImplantedEmbryosDialogVisible(false)}
        onConfirm={handleAddImplantedEmbryosConfirm}
      />
    </div>
  );
};

export function getCurrentColumnDefs(
  activeTab: TabRoute,
  currentColumnDefs: (typeof tabConfig)[number]['columnDefs'] | undefined,
  embryoInventoryColumns: (typeof tabConfig)[number]['columnDefs'],
  ivfTransitColumns: (typeof tabConfig)[number]['columnDefs'],
) {
  return activeTab === 'embryos'
    ? embryoInventoryColumns
    : activeTab === 'ivf-transit-embryos'
      ? ivfTransitColumns
      : currentColumnDefs;
}

export const EmbryoAppPage = () => {
  return (
    <EmbryoAppProvider>
      <EmbryoAppContent />
    </EmbryoAppProvider>
  );
};
